/**
 * 语言、时区设置
 */
import React, { useImperativeHandle, useRef, useState } from 'react';
import { Modal, Form, Select, Switch, Spin } from 'antd';

import { UserIntlStorage, getCurrentIntlConfig } from '@/utils/intl';
import { updateUserIntlConfig, getSysTimeZoneList, getSysLanguageList } from '@/api/login';

import SettingLangConfig from './lang';
import styles from './index.module.scss';

export interface IntlSettingModalRef {
  showModal: () => void;
}

interface IProps {
  type: 'local' | 'cloud';
}

interface FormValue {
  Language: string; // 语言
  TimeZone: number; // 时区
  DSTStatus: boolean; // 夏令时
}

export default React.forwardRef<IntlSettingModalRef, IProps>((props, ref) => {
  const [modalState, setModalState] = useState({
    visible: false,
    loading: false,
  });
  const [modalData, setModalData] = useState({
    languageList: [],
    timezoneList: [],
  });
  const [langData, setLangData] = useState(SettingLangConfig.zh_CN);
  const [formField] = Form.useForm<FormValue>();
  const cacheValueRef = useRef<FormValue>();

  // 语言列表
  const getLanguageList = () => {
    return getSysLanguageList()
      .then((res) => {
        if (res && res.data) {
          return res.data.map((item) => ({
            label: item.localName,
            value: item.language,
            detail: item,
          }));
        }
        return [];
      })
      .catch(() => []);
  };

  // 时区列表
  const getTimeZoneList = (language) => {
    return getSysTimeZoneList({ language })
      .then((res) => {
        if (res && res.data) {
          return res.data.map((item) => ({
            label: `${item.timeZoneCode} ${item.region}`,
            value: item.timeZoneId,
            detail: item,
          }));
        }
        return [];
      })
      .catch(() => []);
  };

  // 更新时区列表
  const updateTimeZoneList = async (language) => {
    setModalState({ ...modalState, loading: true });
    try {
      const timezoneList = await getTimeZoneList(language);
      setModalData({ ...modalData, timezoneList });
    } catch (_) {
      //
    }
    setModalState({ ...modalState, loading: false });
  };

  useImperativeHandle(
    ref,
    () => ({
      showModal: async () => {
        setModalState({ visible: true, loading: true });
        try {
          const currentConfig = await getCurrentIntlConfig();
          const [languageList, timezoneList] = await Promise.all([
            getLanguageList(),
            getTimeZoneList(currentConfig.Language),
          ]);
          formField.resetFields();
          setModalData({ languageList, timezoneList });
          cacheValueRef.current = currentConfig;
          if (currentConfig) {
            const { Language, TimeZone, DSTStatus } = currentConfig;
            formField.setFieldsValue({
              Language: languageList.some((item) => item.value === Language) ? Language : null,
              TimeZone: timezoneList.some((item) => item.value === TimeZone) ? TimeZone : null,
              DSTStatus: DSTStatus || false,
            });
            setLangData(SettingLangConfig[Language]);
          }
          setModalState({ visible: true, loading: false });
        } catch (e) {
          //
        }
      },
    }),
    [],
  );

  // 修改语言
  const handleLangChange = (val) => {
    setLangData(SettingLangConfig[val]);
    updateTimeZoneList(val);
  };

  // 取消
  const handleCancelClick = () => {
    setModalState({
      ...modalState,
      visible: false,
    });
  };

  // 确定
  const handleConfirmClick = async () => {
    setModalState({
      ...modalState,
      loading: true,
    });
    try {
      const formValue = await formField.validateFields();
      const valueKeys = ['Language', 'TimeZone', 'DSTStatus'];
      const currentValue = cacheValueRef.current || {};
      // 数据有变更继续操作
      if (
        formValue.Language &&
        formValue.TimeZone &&
        valueKeys.some((fieldKey) => currentValue[fieldKey] !== formValue[fieldKey])
      ) {
        const { Language, TimeZone, DSTStatus } = formValue;
        const timeItem = modalData.timezoneList.find((item) => item.value === TimeZone);
        const { timeZoneCode } = timeItem.detail;
        if (props.type === 'cloud') {
          await updateUserIntlConfig({
            daylightTimeFlag: DSTStatus,
            language: Language,
            timeZoneCode,
            timeZoneId: TimeZone,
            scene: 'FE_MANUAL_UPDATE',
          });
        }
        UserIntlStorage.setLocalConfig({
          Language,
          TimeZone,
          TimeZoneCode: timeZoneCode,
          DSTStatus,
        });
        window.location.reload();
      }
    } catch (e) {
      //
    }
    setModalState({
      ...modalState,
      loading: false,
    });
  };

  const getPopupContainer = (node) => {
    if (node) return node.parentElement;
    return null;
  };

  return (
    <Modal
      zIndex={9999}
      open={modalState.visible}
      title={langData.setting}
      centered
      maskClosable={false}
      cancelText={langData.cancel}
      okText={langData.confirm}
      wrapClassName={styles.intlSettingModal}
      onCancel={handleCancelClick}
      onOk={handleConfirmClick}
      modalRender={(modal) => {
        return <Spin spinning={modalState.loading}>{modal}</Spin>;
      }}
    >
      <Form name="intl-setting-form" form={formField}>
        <Form.Item label={langData.langLabel} name="Language">
          <Select
            options={modalData.languageList}
            onChange={handleLangChange}
            getPopupContainer={getPopupContainer}
          />
        </Form.Item>
        <Form.Item label={langData.tzLabel} name="TimeZone">
          <Select options={modalData.timezoneList} getPopupContainer={getPopupContainer} disabled />
        </Form.Item>
        <Form.Item label={langData.dstLabel} name="DSTStatus" valuePropName="checked">
          <Switch
            checkedChildren={langData.dstChecked}
            unCheckedChildren={langData.dstUnchecked}
            disabled
          />
        </Form.Item>
      </Form>
    </Modal>
  );
});
